import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { AttachMoney, Message as MessageIcon } from '@material-ui/icons'

import { COLLECTION_STATUS, DRAWER_TYPES, strings } from '@constants'
import { Spacings, Text } from '@styles'
import { getPaymentReceivablesById } from '@data'
import { addPaymentToCollection, changeStatusPaymentReceivables } from '@data/conn/put/ledger'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { displayAmount, showDateFormat } from '@utils'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE, TimeLine } from '@base'
import { Button, Input, Select } from '@baseV2'

import { InputsWrapper } from '../styles'

const RemarkDiv = styled.div`
	font-size: ${Text.SMALL};
	& > span:first-child {
		font-weight: 600;
	}
`

const AttachWrapper = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};

	/* the Input's own container — let it take the free space */
	& > div:first-child {
		flex: 1;
	}
`

// the input is taller than the button (it carries a label), so the button gets its own
// full-height box and centres itself inside it
const ButtonWrapper = styled.div`
	display: flex;

	align-items: center;
	flex: 0 0 auto;
	margin-top: ${Spacings.SPACING_4B};
`

const collectionStatusType = Object.entries(COLLECTION_STATUS).map(([key, value]) => ({ key, label: value }))

const CollectionJourney = ({ row, update }) => {
	const dispatch = useDispatch()
	const [journeyList, setJourneyList] = useState()
	const [paymentReceivables, setPaymentReceivables] = useState()
	const [collectionStatus, setCollectionStatus] = useState(row?.status)
	const [newInvoiceNumber, setNewInvoiceNumber] = useState('')

	const journeyText = (amount, message, date) => (
		<>
			<RemarkDiv>
				<span>{strings('amount', ': ')}</span>
				<span> {displayAmount(amount)}</span>
			</RemarkDiv>
			<RemarkDiv>
				<span>{strings('message', ': ')} </span>
				<span>{message}</span>
			</RemarkDiv>
			<RemarkDiv>
				<span>{strings(['payment', 'date', ': '])} </span>
				<span>{showDateFormat(new Date(date))}</span>
			</RemarkDiv>
		</>
	)

	const collectionJourneyState = (data) => ({
		text: journeyText(data.amount, data.message, data.expected_payment_date),
		paymentType: data?.type === 'payment',
		icon: data?.type === 'payment' ? <AttachMoney /> : <MessageIcon />,
	})

	useEffect(() => {
		getPaymentReceivablesById(row.receivable_id)
			.then((response) => {
				setPaymentReceivables(response)
				setJourneyList(response?.remarks?.map((item) => ({ ...item, date: item.created_at, journeyState: collectionJourneyState(item) })))
			})
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('journey_not_found'), { type: TOAST_TYPE.ERROR }))
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const journeyClickHandler = (item, index, isEditRemark) => {
		// a remark linked to a payment opens that payment instead of the remark editor
		if (item?.document_number) {
			dispatch(toggleDrawer(true, DRAWER_TYPES.EDIT_PAYMENT, { documentNumber: item.document_number, update }))
			return
		}
		dispatch(
			toggleDrawer(true, DRAWER_TYPES.ADD_COLLECTION_V2, {
				paymentReceivables,
				remarkIndex: index,
				data: { ...item },
				isEdit: true,
				update,
				isEditRemark,
			})
		)
	}

	const addRemarkHandler = () => {
		dispatch(toggleDrawer(true, DRAWER_TYPES.ADD_COLLECTION_V2, { paymentReceivables: row, isAddRemark: true, update }))
	}

	const collectionStatusChange = (value) => {
		setCollectionStatus(value.key)
		changeStatusPaymentReceivables(row?.receivable_id, value.key)
			.then(() => update())
			.catch((err) => console.log(err))
	}

	const attachInvoice = () => {
		dispatch(showLoader(true))
		addPaymentToCollection(paymentReceivables?.receivable_id, newInvoiceNumber)
			.then(() => dispatch(showToast(true, strings('msg_invoice_attached_in_collection_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((error) => {
				const error_msg = error?.data?.message ?? strings('msg_invoice_attached_in_collection_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Select
						id='collection-status-v2'
						label={strings('status')}
						placeholder={strings(['status'])}
						data={collectionStatusType}
						displayKey='label'
						primaryKey='key'
						value={collectionStatusType.find((item) => item.key === collectionStatus) || {}}
						onChange={collectionStatusChange}
					/>
					{['PARTIALLY_PAID', 'PENDING', 'RESCHEDULED'].includes(paymentReceivables?.status) && (
						<AttachWrapper>
							<Input
								id='new-invoice-v2'
								label={strings('invoice_number')}
								placeholder={strings('enter', 'invoice_number')}
								value={newInvoiceNumber}
								onChange={(e) => setNewInvoiceNumber(e.target.value.trim())}
							/>
							<ButtonWrapper>
								<Button color='primary' size='small' disabled={!newInvoiceNumber} onClick={attachInvoice}>
									{strings('attach_invoice')}
								</Button>
							</ButtonWrapper>
						</AttachWrapper>
					)}
					<TimeLine
						isCollection={true}
						data={journeyList}
						clickhandler={(item, index, isEditRemark) => journeyClickHandler(item, index, isEditRemark)}
						journeySourceId={row.receivable_id}
					/>
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' onClick={addRemarkHandler}>
					{strings('add', 'remarks')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default CollectionJourney
